import React, { useState, useEffect } from 'react';
import { config } from '../config';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../services/api';
import { useNotification } from '../components/Notification';
import { getCurrencySymbol } from '../utils/currency';
import DataTable from '../components/DataTable';
import IconButton from '../components/IconButton';
import SearchableSelect from '../components/SearchableSelect';
import { generateMobileDeLink } from '../utils/mobileDeLink';
import './Fleet.css';

function Fleet({ tenantId }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [tags, setTags] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [showSellForm, setShowSellForm] = useState(null);
  const [sellData, setSellData] = useState({
    customerId: '',
    salePrice: 0,
    saleDate: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (tenantId) {
      loadTenant();
      loadVehicles();
      loadCustomers();
      loadTags();
    }
  }, [tenantId, location.pathname]);

  const loadTenant = async () => {
    try {
      const data = await api.get('/tenants', null);
      const currentTenant = data.find(t => t.id === tenantId) || data[0];
      setTenant(currentTenant);
    } catch (error) {
      console.error('Error loading tenant:', error);
    }
  };

  const currency = getCurrencySymbol(tenant?.taxSystem || 'DE');

  const loadVehicles = async () => {
    try {
      const data = await api.get('/cars?fleetOnly=true', tenantId);
      setVehicles(data);
    } catch (error) {
      console.error('Error loading vehicles:', error);
      showNotification('Fehler beim Laden der Fahrzeuge', 'error');
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await api.get('/customers', tenantId);
      setCustomers(data);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadTags = async () => {
    try {
      const data = await api.get('/tags', tenantId);
      setTags(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading tags:', error);
      setTags([]);
    }
  };


  const handleSell = async (e) => {
    e.preventDefault();
    try {
      if (!sellData.customerId) {
        showNotification('Bitte wählen Sie einen Kunden aus', 'error');
        return;
      }
      await api.post(`/cars/${showSellForm.id}/sell`, sellData, tenantId);
      showNotification('Fahrzeug erfolgreich verkauft', 'success');
      setShowSellForm(null);
      setSellData({
        customerId: '',
        salePrice: 0,
        saleDate: new Date().toISOString().split('T')[0]
      });
      loadVehicles();
    } catch (error) {
      console.error('Error selling vehicle:', error);
      showNotification(error.message || 'Fehler beim Verkauf des Fahrzeugs', 'error');
    }
  };

  const handleEdit = (vehicle) => {
    navigate(`/fleet/${vehicle.id}/edit`);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Fahrzeug wirklich löschen?')) {
      try {
        await api.delete(`/cars/${id}`, tenantId);
        showNotification('Fahrzeug erfolgreich gelöscht', 'success');
        loadVehicles();
      } catch (error) {
        console.error('Error deleting vehicle:', error);
        showNotification('Fehler beim Löschen des Fahrzeugs', 'error');
      }
    }
  };

  const handleDownloadPurchaseContract = async (vehicle) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL || 'https://api.garagedesk.net/api'}/cars/${vehicle.id}/purchase-contract`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Id': tenantId
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Fehler beim Generieren des Kaufvertrags');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Kaufvertrag-${vehicle.make || ''}-${vehicle.model || ''}-${vehicle.licensePlate || 'unknown'}.pdf`.replace(/\s+/g, '-');
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showNotification('Kaufvertrag erfolgreich heruntergeladen', 'success');
    } catch (error) {
      console.error('Error downloading purchase contract:', error);
      showNotification(error.message || 'Fehler beim Herunterladen des Kaufvertrags', 'error');
    }
  };

  const getCustomerName = (customerId) => {
    if (!customerId) return '-';
    const customer = customers.find(c => c.id === customerId);
    return customer ? `${customer.firstName} ${customer.lastName}` : '-';
  };

  const isInventory = location.pathname === '/fleet/inventory' || location.pathname === '/fleet';
  const isSales = location.pathname === '/fleet/sales';
  
  // Fahrzeuge ohne Status werden als 'owned' behandelt
  const ownedVehicles = vehicles.filter(v => !v.status || v.status === 'owned');
  const soldVehicles = vehicles.filter(v => v.status === 'sold');
  const displayedVehicles = isSales ? soldVehicles : ownedVehicles;
  
  const totalPurchaseValue = ownedVehicles.reduce((sum, v) => sum + (v.purchasePrice || 0), 0);
  const totalMarketValue = ownedVehicles.reduce((sum, v) => sum + (v.marketValue || 0), 0);
  const totalSalesValue = soldVehicles.reduce((sum, v) => sum + (v.salePrice || 0), 0);

  return (
    <div className="fleet">
      <div className="page-header">
        <h1>{isSales ? 'Verkäufe' : 'Bestand'}</h1>
        {isInventory && (
          <button type="button" onClick={() => navigate('/fleet/new')} className="btn-primary">
            Neues Fahrzeug
          </button>
        )}
      </div>

      {isInventory && (
        <div className="fleet-stats">
          <div className="stat-card">
            <div className="stat-label">Eigene Fahrzeuge</div>
            <div className="stat-value">{ownedVehicles.length}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Gesamter Ankaufswert</div>
            <div className="stat-value">{totalPurchaseValue.toFixed(2)} {currency}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Gesamter Marktwert</div>
            <div className="stat-value">{totalMarketValue.toFixed(2)} {currency}</div>
          </div>
        </div>
      )}
      
      {isSales && (
        <div className="fleet-stats">
          <div className="stat-card">
            <div className="stat-label">Verkaufte Fahrzeuge</div>
            <div className="stat-value">{soldVehicles.length}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Gesamter Verkaufswert</div>
            <div className="stat-value">{totalSalesValue.toFixed(2)} {currency}</div>
          </div>
        </div>
      )}

      {showSellForm && (
        <div className="modal">
          <div className="modal-content">
            <h2>Fahrzeug verkaufen: {showSellForm.make} {showSellForm.model}</h2>
            <form onSubmit={handleSell}>
              <div className="form-group">
                <label>Kunde</label>
                <SearchableSelect
                  options={customers.map(c => ({ value: c.id, label: `${c.firstName} ${c.lastName}` }))}
                  value={sellData.customerId}
                  onChange={(value) => setSellData({ ...sellData, customerId: value })}
                  placeholder="Kunde auswählen"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Verkaufspreis ({currency})</label>
                  <input
                    type="number"
                    step="0.01"
                    value={sellData.salePrice}
                    onChange={(e) => setSellData({ ...sellData, salePrice: parseFloat(e.target.value) || 0 })}
                    min="0"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Verkaufsdatum</label>
                  <input
                    type="date"
                    value={sellData.saleDate}
                    onChange={(e) => setSellData({ ...sellData, saleDate: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  Verkaufen
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowSellForm(null);
                    setSellData({
                      customerId: '',
                      salePrice: 0,
                      saleDate: new Date().toISOString().split('T')[0]
                    });
                  }}
                  className="btn-secondary"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DataTable
        data={displayedVehicles}
        columns={[
          {
            key: 'actions',
            header: '',
            sortable: false,
            width: '120px',
            render: (row) => (
              <div style={{ display: 'inline-flex', gap: '0.25rem', whiteSpace: 'nowrap' }}>
                {row.status === 'owned' && (
                  <IconButton
                    icon="sell"
                    onClick={() => setShowSellForm(row)}
                    title="Verkaufen"
                    className="success"
                  />
                )}
                {row.status === 'sold' && (
                  <IconButton
                    icon="pdf"
                    onClick={() => handleDownloadPurchaseContract(row)}
                    title="Kaufvertrag PDF"
                    className="info"
                  />
                )}
                <IconButton
                  icon="edit"
                  onClick={() => handleEdit(row)}
                  title="Bearbeiten"
                />
                <IconButton
                  icon="delete"
                  onClick={() => handleDelete(row.id)}
                  title="Löschen"
                  className="danger"
                />
              </div>
            )
          },
          {
            key: 'make',
            header: 'Marke',
            sortType: 'string',
            accessor: (row) => row.make || '',
            render: (row) => {
              const mainContent = `${row.make || ''} ${row.model || ''}`.trim();
              const mobileDeUrl = generateMobileDeLink(row);
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <div>{mainContent}</div>
                  {row.tags && row.tags.length > 0 && (
                    <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
                      {row.tags.map(tagId => {
                        const tag = tags.find(t => t.id === tagId);
                        if (!tag) return null;
                        return (
                          <span
                            key={tagId}
                            style={{
                              fontSize: '0.7rem',
                              padding: '0.15rem 0.4rem',
                              borderRadius: '20px',
                              backgroundColor: tag.color,
                              color: '#000',
                              border: `1.5px solid ${tag.color}`,
                              fontWeight: 600
                            }}
                          >
                            {tag.name}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {mobileDeUrl && (
                    <div style={{ marginTop: '0.25rem' }}>
                      <a
                        href={mobileDeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          fontSize: '0.75rem',
                          color: '#2196f3',
                          textDecoration: 'none'
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        Auf mobile.de suchen →
                      </a>
                    </div>
                  )}
                </div>
              );
            }
          },
          {
            key: 'year',
            header: 'Baujahr',
            sortType: 'number',
            accessor: (row) => row.year || 0
          },
          {
            key: 'licensePlate',
            header: 'Kennzeichen',
            sortType: 'string',
            accessor: (row) => row.licensePlate || ''
          },
          {
            key: 'mileage',
            header: 'Kilometerstand',
            sortType: 'number',
            align: 'right',
            accessor: (row) => row.mileage || 0,
            render: (row) => `${(row.mileage || 0).toLocaleString('de-DE')} km`
          },
          {
            key: 'purchasePrice',
            header: isSales ? 'Ankaufspreis' : 'Ankaufspreis',
            sortType: 'number',
            align: 'right',
            accessor: (row) => row.purchasePrice || 0,
            render: (row) => `${(row.purchasePrice || 0).toFixed(2)} ${currency}`
          },
          {
            key: 'marketValue',
            header: isSales ? 'Verkaufspreis' : 'Marktwert',
            sortType: 'number',
            align: 'right',
            accessor: (row) => isSales ? (row.salePrice || 0) : (row.marketValue || 0),
            render: (row) => {
              const value = isSales ? (row.salePrice || 0) : (row.marketValue || 0);
              return `${value.toFixed(2)} ${currency}`;
            }
          },
          ...(isSales ? [{
            key: 'soldDate',
            header: 'Verkaufsdatum',
            sortType: 'date',
            accessor: (row) => row.soldDate || '',
            render: (row) => row.soldDate ? new Date(row.soldDate).toLocaleDateString('de-DE') : '-'
          }] : []),
          ...(isInventory ? [{
            key: 'status',
            header: 'Status',
            sortType: 'string',
            render: (row) => (
              <span className={`status-badge status-${row.status || 'owned'}`}>
                {row.status === 'sold' ? 'Verkauft' : 'Eigen'}
              </span>
            )
          }] : []),
          {
            key: 'customerId',
            header: 'Kunde',
            sortType: 'string',
            accessor: (row) => getCustomerName(row.customerId),
            render: (row) => isSales ? getCustomerName(row.customerId) : (row.status === 'sold' ? getCustomerName(row.customerId) : '-')
          },
        ]}
        defaultSort={{ key: 'purchaseDate', direction: 'desc' }}
        emptyMessage="Keine Fahrzeuge vorhanden"
      />
    </div>
  );
}

export default Fleet;

